import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldAlert, ChevronDown, ChevronUp, Archive, ArchiveRestore, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const usd = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
const RISKS = ["Low Risk", "Medium Risk", "High Risk"];
const ELIG = ["Approved", "Approved With Deposit", "Prepayment Required", "Weekly Rentals Only", "Daily Rentals Only", "Manager Approval Required", "Do Not Rent"];
const riskColor = (r) => r === "High Risk" ? "text-red-400 border-red-500/50 bg-red-500/10"
  : r === "Medium Risk" ? "text-amber-300 border-amber-400/50 bg-amber-400/10"
  : "text-emerald-300 border-emerald-500/40 bg-emerald-500/10";
const inp = "px-2 py-1.5 rounded-md bg-[#0A0A0A] border border-white/15 text-white text-xs outline-none focus:border-[#F2B705]";

export default function LatePayRenters({ token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [renters, setRenters] = useState([]);
  const [archivedCount, setArchivedCount] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [history, setHistory] = useState({});

  const load = async (includeArchived = showArchived) => {
    try {
      const { data } = await axios.get(`${API}/leakage/renters?include_archived=${includeArchived}`, auth);
      setRenters(data.renters || []);
      setArchivedCount(data.archived_count || 0);
    } catch { toast.error("Failed to load late-payment renters."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(showArchived); /* eslint-disable-next-line */ }, [showArchived]);

  const archive = async (r, archived) => {
    if (archived && !window.confirm(`Archive ${r.customer}'s late-payment profile? It will be hidden from this list (history and risk settings are kept). You can restore it anytime with "Show archived".`)) return;
    try {
      await axios.post(`${API}/leakage/renters/${r.key}/archive`, { archived }, auth);
      toast.success(archived ? `${r.customer} archived.` : `${r.customer} restored.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update."); }
  };

  const setRisk = async (r, field, value) => {
    try {
      const reason = field === "risk_override" || value === "Do Not Rent"
        ? (window.prompt(`Reason for setting ${field === "risk_override" ? "risk" : "eligibility"} to "${value || "Auto"}"?`) || "")
        : "";
      await axios.post(`${API}/leakage/renters/${r.key}`, { [field]: value, notes: reason }, auth);
      toast.success("Renter profile updated.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update."); }
  };

  const del = async (r, incidentId = "") => {
    const what = incidentId ? "this late-payment incident" : `${r.customer}'s ENTIRE late-payment profile (${r.incidents} incident${r.incidents !== 1 ? "s" : ""})`;
    if (!window.confirm(`Permanently delete ${what}? This cannot be undone.`)) return;
    const pwd = window.prompt("Management only — enter the management password to confirm this deletion:");
    if (!pwd) return;
    try {
      const { data } = await axios.post(`${API}/leakage/renters/${r.key}/delete`, { management_password: pwd, incident_id: incidentId }, auth);
      toast.success(data.scope === "profile" ? `${r.customer}'s profile deleted (${data.deleted} incident${data.deleted !== 1 ? "s" : ""} removed).` : "Incident deleted.");
      setHistory((h) => ({ ...h, [r.key]: undefined }));
      if (data.scope === "incident" && open === r.key) {
        try {
          const res = await axios.get(`${API}/leakage`, { ...auth, params: { range: "custom", date_from: "2020-01-01", key: r.key } });
          setHistory((h) => ({ ...h, [r.key]: res.data }));
        } catch { /* noop */ }
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete."); }
  };

  const toggle = async (r) => {
    if (open === r.key) { setOpen(null); return; }
    setOpen(r.key);
    if (!history[r.key]) {
      try {
        const { data } = await axios.get(`${API}/leakage`, { ...auth, params: { range: "custom", date_from: "2020-01-01", key: r.key } });
        setHistory((h) => ({ ...h, [r.key]: data }));
      } catch { /* noop */ }
    }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#F2B705]" size={22} /></div>;

  return (
    <div className="space-y-3" data-testid="late-pay-renters">
      {archivedCount > 0 && (
        <div className="flex justify-end">
          <button data-testid="renters-show-archived" onClick={() => setShowArchived((s) => !s)}
            className="flex items-center gap-1.5 text-xs font-semibold border border-white/15 text-white/60 px-3 py-1.5 rounded hover:border-[#F2B705] hover:text-[#F2B705] transition-colors">
            <Archive size={13} /> {showArchived ? "Hide archived" : `Show archived (${archivedCount})`}
          </button>
        </div>
      )}
      {!renters.length && <div className="text-center py-10 border border-white/10 rounded-xl bg-[#0A0A0A] text-white/50">No renters with late-payment history yet.</div>}
      {renters.map((r) => (
        <div key={r.key} data-testid={`renter-row-${r.key}`} className={`bg-[#0C0C0C] border rounded-lg p-4 ${r.archived ? "border-white/5 opacity-60" : "border-white/10"}`}>
          <div className="flex flex-wrap justify-between gap-2 cursor-pointer" onClick={() => toggle(r)}>
            <div className="flex items-center gap-2">
              <span className="font-display font-bold">{r.customer}</span>
              <span className="text-white/40 text-sm">{r.phone}</span>
              <span className={`text-[11px] border rounded px-2 py-0.5 ${riskColor(r.risk)}`}>
                <ShieldAlert size={10} className="inline mr-1" />{r.risk}{r.risk_overridden && " (manual)"}
              </span>
              {r.eligibility !== "Approved" && <span className="text-[11px] border border-orange-400/50 text-orange-300 bg-orange-400/10 rounded px-2 py-0.5">{r.eligibility}</span>}
              {r.archived && <span className="text-[11px] border border-white/20 text-white/50 rounded px-2 py-0.5">Archived{r.archived_at ? ` ${String(r.archived_at).slice(0, 10)}` : ""}</span>}
            </div>
            <div className="text-sm flex items-center gap-3">
              {r.outstanding > 0 && <span className="text-red-400 font-bold">{usd(r.outstanding)} owed</span>}
              {open === r.key ? <ChevronUp size={15} className="text-white/40" /> : <ChevronDown size={15} className="text-white/40" />}
            </div>
          </div>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-1 text-xs text-white/50">
            <span>Rentals: <b className="text-white/80">{r.rentals}</b></span>
            <span>Incidents: <b className="text-white/80">{r.incidents}</b></span>
            <span>Days late: <b className="text-white/80">{r.total_days_late}</b></span>
            <span>Courtesy days: <b className="text-white/80">{r.courtesy_days}</b></span>
            <span>Paid late: <b className="text-white/80">{usd(r.paid_late)}</b></span>
            <span>Waived: <b className="text-white/80">{usd(r.waived)}</b></span>
            <span>Lost: <b className={r.lost > 0 ? "text-orange-300" : "text-white/80"}>{usd(r.lost)}</b></span>
            <span>Broken promises: <b className={r.broken_promises > 0 ? "text-red-400" : "text-white/80"}>{r.broken_promises}</b></span>
            <span>Recoveries: <b className="text-white/80">{r.recoveries}</b></span>
            <span>Last incident: <b className="text-white/80">{r.last_incident || "—"}</b></span>
          </div>
          <div className="mt-2 flex flex-wrap gap-2 items-center">
            <label className="text-[10px] uppercase text-white/40">Risk</label>
            <select data-testid={`renter-risk-${r.key}`} className={inp} value={r.risk_overridden ? r.risk : ""}
              onChange={(e) => setRisk(r, "risk_override", e.target.value)} onClick={(e) => e.stopPropagation()}>
              <option value="">Auto ({r.auto_risk})</option>
              {RISKS.map((x) => <option key={x}>{x}</option>)}
            </select>
            <label className="text-[10px] uppercase text-white/40 ml-2">Eligibility</label>
            <select data-testid={`renter-elig-${r.key}`} className={inp} value={r.eligibility}
              onChange={(e) => setRisk(r, "eligibility", e.target.value)} onClick={(e) => e.stopPropagation()}>
              {ELIG.map((x) => <option key={x}>{x}</option>)}
            </select>
            {r.archived ? (
              <button data-testid={`renter-restore-${r.key}`} onClick={(e) => { e.stopPropagation(); archive(r, false); }}
                className="ml-auto flex items-center gap-1.5 text-[11px] font-semibold border border-emerald-400/40 text-emerald-300 px-2.5 py-1 rounded hover:bg-emerald-400 hover:text-black transition-colors">
                <ArchiveRestore size={12} /> Restore
              </button>
            ) : (
              <button data-testid={`renter-archive-${r.key}`} onClick={(e) => { e.stopPropagation(); archive(r, true); }}
                className="ml-auto flex items-center gap-1.5 text-[11px] font-semibold border border-white/15 text-white/50 px-2.5 py-1 rounded hover:border-red-400/60 hover:text-red-300 transition-colors">
                <Archive size={12} /> Archive
              </button>
            )}
            <button data-testid={`renter-delete-${r.key}`} onClick={(e) => { e.stopPropagation(); del(r); }}
              title="Management only — permanently delete this profile"
              className="flex items-center gap-1.5 text-[11px] font-semibold border border-red-500/40 text-red-400 px-2.5 py-1 rounded hover:bg-red-500 hover:text-white transition-colors">
              <Trash2 size={12} /> Delete
            </button>
          </div>
          {open === r.key && (
            <div className="mt-3 border-t border-white/10 pt-3 space-y-2" data-testid={`renter-history-${r.key}`}>
              <div className="text-white/40 uppercase tracking-wide font-bold text-xs">Complete Late-Payment History</div>
              {(history[r.key] || []).map((rec) => (
                <div key={rec.id} className="text-xs text-white/60 bg-[#0A0A0A] border border-white/10 rounded px-3 py-2 flex flex-wrap justify-between items-center gap-2">
                  <span>{String(rec.overdue_since).slice(0, 10)} · {rec.vehicle} · <b className="text-white/85">{rec.status}</b></span>
                  <span className="flex items-center gap-2">
                    <span>{rec.unpaid_days}d late · owed {usd(rec.total_owed)} · collected {usd(rec.collected)} · lost <b className={rec.lost_revenue > 0 ? "text-orange-300" : ""}>{usd(rec.lost_revenue)}</b></span>
                    <button data-testid={`incident-delete-${rec.id}`} onClick={(e) => { e.stopPropagation(); del(r, rec.id); }}
                      title="Management only — delete this incident"
                      className="text-red-400/70 hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
                  </span>
                </div>
              ))}
              {!(history[r.key] || []).length && <div className="text-white/35 text-xs">Loading history…</div>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
